import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Loader, ErrorFallback, TextInput, InputSwitch } from 'smart-react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import { Error } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import {
  buildNotification,
  GenerateNotification,
  isValidate,
  isFromValid,
  DescriptionTextArea,
  DropDownInput,
} from 'smart-react';
import {
  listRunSet,
  createRunSet,
  updateRunSet,
} from '../Services/RunSetService';
import {
  base64ToObject,
  objectToBase64,
} from '../../../../Utils/Common/commonUtils';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Form, Field } from '@progress/kendo-react-form';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../constants/eventDataTypes';
import { LOOKUP_TYPES } from '../../../../constants/applicationConstants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../Core/Context/AuthContext';
import { SplitButton, SplitButtonItem } from '@progress/kendo-react-buttons';
import DesktopLayout from '../Components/Layout/DesktopLayout';
import TenantDropdown from '../../../Core/Components/TenantDropdown/TenantDropdown';
import NoRecordFound from '../../../Core/Components/NoRecordFound/NoRecordFound';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import { retrieveAndDecrypt } from '../../../../Utils/Storage/localStorageUtil';
import {
  ValidatePermissions,
  findTenant,
  findTenantById,
} from '../../../../Utils/Auth/ValidatePermissions';
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
} from '../../../../constants/notificationMessages';
/**
 * Add Run Set main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add Run Set component.
 */
const AddRunSet = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const { getParam } = useQueryParams();
  const refId = getParam('refId');
  const { tenantID, getTokensFromStorage } = useAuth(); // Get tenantID using Auth Context
  const [tenant, setTenant] = React.useState(findTenantById(tenantID));
  const [isTextArea, setIsTextArea] = React.useState(false);
  const [editorField, setEditorField] = React.useState({
    name: '',
    value: '',
    label: '',
  });
  const [shouldUpdate, setShouldUpdate] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [noRecord, setNoRecord] = React.useState(false);
  const [formData, setFormData] = React.useState({
    tenant_id: tenantID,
    TenantName: tenant?.TenantName,
    uc_ossi_run_id: '',
    uc_env_id: '%',
    uc_ossi_client_id: '',
    uc_ossi_grp: '',
    uc_ossi_sleep_sec: '',
    uc_ossi_parallel_flg: '',
    uc_ossi_disable_flg: false,
    uc_ossi_descr: '',
  });
  const requiredFields = ['uc_ossi_run_id', 'uc_ossi_descr', 'TenantName'];
  const [selected, setSelected] = React.useState(
    getParam('runSetArgs') ? 1 : 0,
  );
  const [validateForm, setValidateForm] = React.useState(false);
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const groupTypes = retrieveAndDecrypt(LOOKUP_TYPES?.groupTypes);

  const { state } = useLocation();
  const [testState, setTestState] = React.useState(state);
  const handleSaveSplit = (e) => {
    const { itemIndex } = e;
    switch (itemIndex) {
      case 0: {
        onSave({ isExit: true });
        break;
      }
      case 1:
        onSave({ isContinue: true });
      default: {
      }
    }
  };

  /**
   * Handle Selected Tab
   * @param {*} e
   */
  const handleSelect = (e) => {
    setSelected(e.selected);
    setShouldUpdate(!shouldUpdate);
  };

  /**
   * fetch Run Set Data From API
   * @param {*} stateData
   */
  const fetchRunSetData = async (stateData) => {
    const { accessToken } = getTokensFromStorage();
    setLoading(true);
    try {
      let refObj = base64ToObject(refId);
      const response = await listRunSet({
        accessToken: accessToken,
        limit: '50',
        offset: 1,
        filter: [
          {
            ColumnName: 'uc_ossi_run_id',
            Op: 'eq',
            ColumnValue: refObj?.uc_ossi_run_id,
          },
          {
            ColumnName: 'tenant_id',
            Op: 'eq',
            ColumnValue: tenantId,
          },
        ],
        moduleName: 'RunSets',
        isServiceWorker: false,
      });

      if (response?.runSet?.length > 0) {
        const runSetData = response.runSet[0];
        setTestState({ ...stateData, dataSet: runSetData, edit: true });
        setFormData(runSetData);
        setTenant(findTenant(runSetData));
        setNoRecord(false);
        if (ValidatePermissions(runSetData)) {
          setDetailsDataModified(true);
        }
      } else {
        setNoRecord(true);
      }
    } catch (error) {
      console.error('Error fetching run set data:', error);
      // Handle error state if needed
      setNoRecord(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (testState || (tenantId && tenantId !== 'new')) {
      const dataSet = state ? state.dataSet : null;
      // Call the async function
      fetchRunSetData(state);

      if (dataSet) {
        // Rest of the synchronous logic
        setFormData({ ...dataSet });
        setTenant(findTenant(dataSet));
        if (ValidatePermissions(dataSet)) {
          setDetailsDataModified(true);
        } else {
          setDetailsDataModified(false);
        }
        setShouldUpdate(!shouldUpdate);
      }
    } else {
      setDetailsDataModified(true);
    }
  }, [state]);

  /**
   * on update the tenantID update the formData
   */
  React.useEffect(() => {
    if (!testState?.edit && tenantId === 'new') {
      let tempTenant = findTenantById(tenantID);
      setTenant(tempTenant);
      setFormData((prevState) => ({
        ...prevState,
        tenant_id: tenantID,
        TenantName: tempTenant?.TenantName,
      }));
    }
  }, [tenantID]);

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  /**
   * onSave
   * submit the remote server data to api
   */
  const onSave = async ({ isExit = false, isContinue = false }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }
    setLoading(true);
    let notificationMetaData = {};
    const data = {
      ...formData,
      uc_ossi_run_id: testState?.edit
        ? formData.uc_ossi_run_id
        : tenant?.OrganizationPrefix + formData.uc_ossi_run_id,
      uc_ossi_disable_flg: formData.uc_ossi_disable_flg ? 1 : 0,
    };
    let response;
    let method;
    if (testState?.edit) {
      method = updateRunSet;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createRunSet;
      notificationMetaData = CREATE_MESSAGE;
      setFormData({
        tenant_id: tenantID,
        TenantName: tenant?.TenantName,
        uc_ossi_run_id: '',
        uc_env_id: '%',
        uc_ossi_client_id: '',
        uc_ossi_grp: '',
        uc_ossi_sleep_sec: '',
        uc_ossi_parallel_flg: '',
        uc_ossi_disable_flg: false,
        uc_ossi_descr: '',
      });
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        moduleName: 'runset',
        isServiceWorker: false,
      });

      if (!response.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        if (isContinue) {
          const payloadData = response?.Payload;
          const refId = objectToBase64({
            uc_ossi_run_id: payloadData?.uc_ossi_run_id,
            tenant_id: payloadData?.tenant_id,
            uc_ossi_prod_id: payloadData?.uc_ossi_prod_id,
          });
          navigate(`/autest/run-set/${payloadData?.tenant_id}?refId=${refId}`, {
            state: {
              edit: true,
              dataSet: payloadData,
            },
          });
        }
        if (isExit) {
          navigate('/autest/run-sets');
        }
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * onExit
   * back to run-sets listing screen
   */
  const onExit = () => {
    navigate('/autest/run-sets');
  };
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto'>
        <React.Suspense fallback={<Loader />}>
          {loading && <Loader />}
          {noRecord ? (
            <NoRecordFound
              recordNotFoundText={
                'No Command found against your selected tenant'
              }
              onExit={onExit}
            />
          ) : (
            <>
              <div className={`${isTextArea ? '' : 'k-d-none'}`}>
                <DescriptionTextArea
                  onChange={handleInputChange}
                  label={'Description'}
                  renderTypes={!detailsDataModified ? 'ReadOnly' : ''}
                  validateForm={validateForm}
                  formData={formData}
                  setFormData={setFormData}
                  detailsDataModified={detailsDataModified}
                  isTextArea={isTextArea}
                  setIsTextArea={setIsTextArea}
                  editorField={editorField}
                  setEditorField={setEditorField}
                  row={2}
                />
              </div>
              <div
                className={`add-test main-page-section add-page-section ${!isTextArea ? '' : 'k-d-none'}`}
              >
                <Card className='tophead'>
                  <CardHeader>
                    <div className='action-buttons-container'>
                      <Link
                        className='action-icon-wrapper !k-d-block'
                        to={`/autest/run-sets`}
                      >
                        <FontAwesomeIcon
                          className='k-mr-2 k-pt-3'
                          icon={faArrowLeft}
                        />
                      </Link>
                      <div>
                        <p className='display-text k-mb-0'>
                          Run Set:{' '}
                          {testState?.edit
                            ? (formData?.uc_ossi_run_id ?? '')
                            : `${tenant?.OrganizationPrefix ?? ''}${formData?.uc_ossi_run_id ?? ''}`}
                        </p>
                        {formData?.uc_ossi_descr && (
                          <span className='k-d-block k-mb-2'>
                            {formData?.uc_ossi_descr}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
                <Card className='k-mt-3 detail-card'>
                  <CardHeader>
                    <h6 className='card-title-secondary'>Details</h6>
                  </CardHeader>
                  <CardBody className='detail-card-body'>
                    <div>
                      <Form
                        onSubmit={onSave}
                        render={(formRenderProps) => (
                          <div className='card-content'>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper ${isValidate(validateForm, formData?.TenantName) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <TenantDropdown
                                  textField='TenantName'
                                  label='Tenant'
                                  dataItemKey='TenantId'
                                  className='tenant-dropdown k-align-self-end'
                                  tenant={tenant}
                                  setTenant={setTenant}
                                  defaultItem={{
                                    TenantName: 'Select Tenant ...',
                                    disabled: true,
                                  }}
                                  editMode={testState?.edit ? 'ReadOnly' : ''}
                                  filterable={true}
                                  tenantFormId='tenant_id'
                                  tenantFormName='TenantName'
                                  setFormData={setFormData}
                                  requiredField={true}
                                />
                                {isValidate(
                                  validateForm,
                                  formData?.TenantName,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper ${!testState?.edit && 'prefix-field-wrapper'} ${isValidate(validateForm, formData.uc_ossi_run_id) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <Field
                                  key={'uc_ossi_run_id'}
                                  id={'uc_ossi_run_id'}
                                  name={'uc_ossi_run_id'}
                                  label={'Run Id'}
                                  data={formData?.uc_ossi_run_id}
                                  onChange={handleInputChange}
                                  type={'text'}
                                  component={TextInput}
                                  renderTypes={
                                    testState?.edit ? 'ReadOnly' : ''
                                  }
                                  optional={false}
                                  requiredField={true}
                                  prefix={!testState?.edit ? true : false}
                                  prefixLabel={tenant?.OrganizationPrefix}
                                />
                                {isValidate(
                                  validateForm,
                                  formData.uc_ossi_run_id,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper field-wrapper-description field-wrapper-100 ${isValidate(validateForm, formData?.uc_ossi_descr) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <DescriptionTextArea
                                  onChange={handleInputChange}
                                  label={'Description'}
                                  renderTypes={
                                    !detailsDataModified ? 'ReadOnly' : ''
                                  }
                                  validateForm={validateForm}
                                  formData={formData}
                                  setFormData={setFormData}
                                  detailsDataModified={detailsDataModified}
                                  isTextArea={isTextArea}
                                  setIsTextArea={setIsTextArea}
                                  editorField={{
                                    name: 'uc_ossi_descr',
                                    value: formData?.uc_ossi_descr,
                                    label: 'Description',
                                  }}
                                  setEditorField={setEditorField}
                                  row={2}
                                  requiredField={true}
                                />
                                {isValidate(
                                  validateForm,
                                  formData?.uc_ossi_descr,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div className={`field-wrapper`}>
                                <DropDownInput
                                  label='Group'
                                  textField='DisplayName'
                                  dataItemKey='Value'
                                  name={'uc_ossi_grp'}
                                  dataSet={groupTypes}
                                  value={
                                    formData?.uc_ossi_grp
                                      ? {
                                          Value: formData?.uc_ossi_grp,
                                          DisplayName: formData?.uc_ossi_grp,
                                        }
                                      : null
                                  }
                                  handleChange={(e) => {
                                    setFormData((prevState) => ({
                                      ...prevState,
                                      uc_ossi_grp: e.value?.Value,
                                    }));
                                  }}
                                  defaultItem={{
                                    DisplayName: 'Select Group ...',
                                    Value: '',
                                  }}
                                  filterable={true}
                                />
                              </div>
                            </div>

                            <div className='k-d-flex single-field-row k-mt-4'>
                              <div className='field-wrapper'>
                                <Field
                                  name='uc_ossi_disable_flg'
                                  component={InputSwitch}
                                  label='Disabled'
                                  onLabel='Yes'
                                  offLabel='No'
                                  id='uc_ossi_disable_flg'
                                  checked={formData?.uc_ossi_disable_flg}
                                  onChange={handleInputChange}
                                  disabled={!detailsDataModified}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  </CardBody>
                </Card>
                {detailsDataModified && (
                  <Card className='k-mt-3'>
                    <CardHeader>
                      <div className='k-d-flex single-field-row button-form-wrapper k-action-buttons !k-px-0'>
                        <>
                          <Button
                            icon={'cancel'}
                            onClick={() => onExit()}
                            type='button'
                          >
                            Cancel
                          </Button>
                          <SplitButton
                            text='save'
                            themeColor={'primary'}
                            onButtonClick={() =>
                              onSave({ isExit: false, isContinue: true })
                            }
                            onItemClick={handleSaveSplit}
                          >
                            <SplitButtonItem text='Save & Close' />
                          </SplitButton>
                        </>
                      </div>
                    </CardHeader>
                  </Card>
                )}
                {testState?.edit && (
                  <Card className='k-mt-3 detail-grid-section'>
                    <CardBody className='!k-px-0'>
                      <DesktopLayout
                        handleSelect={handleSelect}
                        selected={selected}
                        detailsDataModified={detailsDataModified}
                        state={testState}
                        shouldUpdate={shouldUpdate}
                        setShouldUpdate={setShouldUpdate}
                      />
                    </CardBody>
                  </Card>
                )}
              </div>
            </>
          )}
        </React.Suspense>
      </div>
    </ErrorBoundary>
  );
};
export default AddRunSet;
